import axios from "axios";
const SDK_LOG_PREFIX = "[VerificationSDK]";
function sdkLog(...args: any[]) {
  try { console.log(SDK_LOG_PREFIX, ...args); } catch {}
}
export interface InitOptions {
  reference?: string; // Unique reference from org
  email?: string;
  callback_url?: string; // Where server posts results
  redirect_url?: string; // Where user is redirected after KYC
  device_type?: "WEB" | "MOBILE"; // Optional
  // Optional default UI configuration for SDK-driven components
  ui?: {
    classNames?: Record<string, string>;
    styles?: Record<string, any>;
  };
}

export interface InitResponseData {
  session_id: string;
  device_type: "WEB" | "MOBILE";
  next_step: string;
  resume: boolean;
  verification_url: string;
  qrcode_url?: string;
  callback_url: string;
  redirect_url: string;
  expired_at: string;
}

export interface InitResponse {
  status: string; // e.g., "success" | "error"
  message: string;
  data: InitResponseData;
}


// Default API base URL (can be overridden via constructor)
// const DEFAULT_API_BASE_URL = "http://192.168.0.33:5001";
const DEFAULT_API_BASE_URL = "https://backenddevmain.astraprotocol.com";

// Utility to detect device type
function detectDeviceType(): "WEB" | "MOBILE" {
  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera || "";
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      return "MOBILE";
    }
  }
  return "WEB";
}

export default class VerificationSDK {
  public serverKey: string;
  private sessionId: string | null = null;
  private deviceType: "WEB" | "MOBILE" = detectDeviceType();
  private qrCodeUrl: string | null = null;
  private uiConfig: { classNames?: Record<string, string>; styles?: Record<string, any> } = {};
  private static defaultUi: { classNames?: Record<string, string>; styles?: Record<string, any> } | null = null;
  private apiBaseUrl: string = DEFAULT_API_BASE_URL;

  constructor({ serverKey, apiBaseUrl }: { serverKey: string; apiBaseUrl?: string }) {
    this.serverKey = serverKey;
    if (apiBaseUrl) this.apiBaseUrl = apiBaseUrl;
    sdkLog("constructor", { hasServerKey: Boolean(serverKey), apiBaseUrl: this.apiBaseUrl });
  }

  /**
   * Expose server key so web can embed it in QR for mobile handoff
   */
  // getServerKey(): string {
  //   sdkLog("getServerKey");
  //   return this.serverKey;
  // }

  getServerKey(): string | null {
    sdkLog("getServerKey", { serverKey: this.serverKey });
    return this.serverKey;
  }
  /**
   * Set session ID for existing sessions (used for mobile continuation)
   */
  setSessionId(sessionId: string): void {
    sdkLog("setSessionId", { sessionId });
    this.sessionId = sessionId;
  }

  /**
   * Get QR code URL for mobile switching (if available from init response)
   */
  getQrCodeUrl(): string | null {
    sdkLog("getQrCodeUrl", { qrCodeUrl: this.qrCodeUrl });
    return this.qrCodeUrl;
  }

  /**
   * Set QR code URL from init response
   */
  setQrCodeUrl(url: string): void {
    sdkLog("setQrCodeUrl", { url });
    this.qrCodeUrl = url;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | null {
    sdkLog("getSessionId", { sessionId: this.sessionId });
    return this.sessionId;
  }

  /**
   * Step 1: Initialize session
   */

  async init(options: InitOptions): Promise<InitResponse> {
    sdkLog("init:start", { options });
    const deviceType = options?.device_type || detectDeviceType();
    this.deviceType = deviceType;
    const hasBody = Boolean(
      options && (options.reference || options.email || options.callback_url || options.redirect_url || options.ui)
    );

    try {
      const res = await axios.post<InitResponse>(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/initiate`,
        hasBody ? options : undefined,
        {
          headers: {
            "Content-Type": "application/json",
            "x-server-key": this.serverKey,
            "device-type": deviceType,
          },
          withCredentials: true,
        }
      );

      const json = res.data;

      if (!json || !json.data || !json.data.session_id) {
        throw new Error("Init failed: Invalid server response");
      }

      this.sessionId = json.data.session_id;
      this.deviceType = json.data.device_type || deviceType;

      if (options.ui) {
        const merged = {
          classNames: { ...(options.ui.classNames || {}) },
          styles: { ...(options.ui.styles || {}) },
        };
        this.uiConfig = merged;
        VerificationSDK.defaultUi = merged;
      } else if (VerificationSDK.defaultUi) {
        this.uiConfig = VerificationSDK.defaultUi;
      }

      if (json.data.qrcode_url) {
        this.setQrCodeUrl(json.data.qrcode_url);
      }

      sdkLog("init:success", { sessionId: this.sessionId, deviceType: this.deviceType, hasQr: Boolean(json.data.qrcode_url) });
      return json;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Init failed";
      sdkLog("init:error", msg);
      throw new Error(`Init failed: ${msg}`);
    }
  }

  /**
   * Step 2: Upload face scan
   */
  async face(faceBlob: Blob): Promise<any> {
    sdkLog("face:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.includes(",") ? result.split(",")[1] : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(faceBlob);
    });

    try {
      const res = await axios.post(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/face`,
        { face_scan_img: base64 },
        {
          headers: {
            "Content-Type": "application/json",
            "x-server-key": this.serverKey,
            "device-type": this.deviceType || detectDeviceType(),
          },
          withCredentials: true,
        }
      );
      sdkLog("face:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Face upload failed";
      sdkLog("face:error", msg);
      throw new Error(`Face upload failed: ${msg}`);
    }
  }

  /**
   * Backwards-compatible alias to match UI usage
   */
  async uploadFace(faceBlob: Blob): Promise<any> {
    return this.face(faceBlob);
  }

  /**
   * Step 3: Upload document (ID_CARD, PASSPORT, etc.)
   */
  async uploadDocument(docBlob: Blob, type: string): Promise<any> {
    sdkLog("uploadDocument:start", { hasSession: Boolean(this.sessionId), type });
    if (!this.sessionId) throw new Error("Session not initialized");

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.includes(",") ? result.split(",")[1] : result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(docBlob);
    });

    try {
      const res = await axios.post(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/docs`,
        { docs_scan_img: base64, docType: type },
        {
          headers: {
            "x-server-key": this.serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("uploadDocument:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Document upload failed";
      sdkLog("uploadDocument:error", msg);
      throw new Error(`Document upload failed: ${msg}`);
    }
  }

  // danish

  /**
   * Step 4: Check verification status
   */
  async getStatus(): Promise<any> {
    sdkLog("getStatus:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");
    try {
      const res = await axios.get(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/status`,
        {
          headers: {
            "x-server-key": this.serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("getStatus:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Status fetch failed";
      sdkLog("getStatus:error", msg);
      throw new Error(`Status fetch failed: ${msg}`);
    }
  }

  /**
   * Step 5: Fetch final KYC result for the session
   */
  async getResult(): Promise<any> {
    sdkLog("getResult:start", { hasSession: Boolean(this.sessionId) });
    if (!this.sessionId) throw new Error("Session not initialized");
    try {
      const res = await axios.put(
        `${this.apiBaseUrl}/api/v2/dashboard/merchant/onsite/session/${this.sessionId}/result`,
        undefined,
        {
          headers: {
            "x-server-key": this.serverKey,
            "device-type": this.deviceType || detectDeviceType(),
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      sdkLog("getResult:success");
      return res.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Result fetch failed";
      sdkLog("getResult:error", msg);
      throw new Error(`Result fetch failed: ${msg}`);
    }
  }

  /**
   * Get default UI configuration provided at init
   */
  getUiConfig(): { classNames?: Record<string, string>; styles?: Record<string, any> } {
    // Prefer per-instance overrides, fallback to global default to keep consistency
    return this.uiConfig || VerificationSDK.defaultUi || {};
  }
}
